import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { MasterCodeSchema } from './query-types.js';

/**
 * Call Status master (Masters → Call Statuses). The outcome of a
 * follow-up call (e.g. "answered", "voicemail", "busy"). Optionally
 * carries a "target stage" pointer: when set, the Follow-Up form
 * shows an "Update Query Status" button that transitions the lead's
 * stage to this `targetStatus` (+ `targetSubStatus` when present).
 * Admins configure this per call status to wire e.g. "Connected" to
 * "Active → Proposal", or "Not Interested" to "Lost". See ADR-0016 /
 * ADR-0020 plus the call-status-target-stage migration.
 */

/// 2026-06-25: target-stage refs were removed - the "Update Query
/// Status" button on the followup form was retired in favor of the
/// LeadStatusChanger flow (ADR-0054). The list response shape no
/// longer carries `targetStatus` / `targetSubStatus`.

export const CallStatusListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isConnected: z.boolean(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type CallStatusListItem = z.infer<typeof CallStatusListItemSchema>;

export const CallStatusListQuerySchema = PaginationQuerySchema.extend({
  isActive: BooleanQueryParamSchema.optional(),
  isConnected: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type CallStatusListQuery = z.infer<typeof CallStatusListQuerySchema>;

export const CallStatusListResponseSchema = paginatedResponseSchema(CallStatusListItemSchema);
export type CallStatusListResponse = z.infer<typeof CallStatusListResponseSchema>;

export const CallStatusDetailSchema = CallStatusListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type CallStatusDetail = z.infer<typeof CallStatusDetailSchema>;

const CallStatusCoreFieldsSchema = z.object({
  code: MasterCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(120),
  description: z.string().trim().max(500).nullable().optional(),
  /** Marks "connected" outcomes (used for connect-rate reports). */
  isConnected: z.boolean().optional(),
  // 2026-06-25: `targetStatusId` + `targetSubStatusId` were removed.
  legacyId: z.number().int().positive().nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateCallStatusRequestSchema = CallStatusCoreFieldsSchema;
export type CreateCallStatusRequest = z.infer<typeof CreateCallStatusRequestSchema>;

export const UpdateCallStatusRequestSchema = CallStatusCoreFieldsSchema.partial();
export type UpdateCallStatusRequest = z.infer<typeof UpdateCallStatusRequestSchema>;
